import { useEffect, useState } from "react";
import { toast } from "sonner";
import { HiOutlineX } from "react-icons/hi";

import FilePreviewModal, {
  detectPreviewKind,
  type FilePreviewKind,
} from "components/FilePreviewModal";
import uploadDropzoneGraphic from "assets/svg/upload-modal-dropzone-graphic.svg";

import {
  getLegalCertificateReceived,
  saveLegalCertificateReceived,
  type LegalCertificateReceivedApplicationItem,
  type LegalCertificateReceivedPreview,
} from "../../api/legalCertificate";
import { generateUploadPresignedUrls } from "../../api/discussion";
import { downloadFileFromUrlWithFallback } from "../../utils/downloadFileFromUrl";

export const LEGAL_CERTIFICATE_STATUS_OF_LEGAL_CERTIFICATE_FORM_ID =
  "legal-certificate-status-of-legal-certificate-form";

const ACCEPTED_TYPES = ".pdf";
const ACCEPTED_HELP = "PDF format, up to 50MB";
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
const UPLOAD_FOLDER_PREFIX = "legal-certificate/status";

const LABEL_FEATURES = { fontFeatureSettings: "'ss01' on, 'cv01' on" } as const;

type DocumentKind = "legalCertificate" | "registrationCertificate";

type RowState = LegalCertificateReceivedApplicationItem & {
  pendingLegalCertificateFilename?: string | null;
  pendingRegistrationCertificateFilename?: string | null;
};

type PreviewState = {
  url: string;
  name: string;
  kind: FilePreviewKind;
};

type UploadingState = Record<string, Record<DocumentKind, boolean>>;

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

function mergeApplications(
  next: LegalCertificateReceivedApplicationItem[],
  prev: RowState[],
): RowState[] {
  const prevById = new Map(prev.map((row) => [row.applicationId, row]));
  return next.map((item) => {
    const prior = prevById.get(item.applicationId);
    return {
      ...item,
      pendingLegalCertificateFilename: prior?.pendingLegalCertificateFilename ?? null,
      pendingRegistrationCertificateFilename:
        prior?.pendingRegistrationCertificateFilename ?? null,
    };
  });
}

export default function LegalCertificateStatusOfLegalCertificateView({
  leadId,
  onSuccess,
}: Props) {
  const [preview, setPreview] = useState<LegalCertificateReceivedPreview | null>(
    null,
  );
  const [rows, setRows] = useState<RowState[]>([]);
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [draggingKey, setDraggingKey] = useState<string | null>(null);
  const [uploadingKeys, setUploadingKeys] = useState<UploadingState>({});
  const [previewState, setPreviewState] = useState<PreviewState | null>(null);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      setPreview(null);
      setRows([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getLegalCertificateReceived(leadId)
      .then((data) => {
        if (cancelled) return;
        setPreview(data);
        setRows((prev) => mergeApplications(data.applications, prev));
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(
          err instanceof Error
            ? err.message
            : "Failed to load Status of Legal Certificate data",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const setUploading = (
    applicationId: string,
    kind: DocumentKind,
    value: boolean,
  ) => {
    setUploadingKeys((prevState) => ({
      ...prevState,
      [applicationId]: {
        legalCertificate: prevState[applicationId]?.legalCertificate ?? false,
        registrationCertificate:
          prevState[applicationId]?.registrationCertificate ?? false,
        [kind]: value,
      },
    }));
  };

  const isUploading = (applicationId: string, kind: DocumentKind): boolean =>
    !!uploadingKeys[applicationId]?.[kind];

  const setPendingFilename = (
    applicationId: string,
    kind: DocumentKind,
    filename: string | null,
  ) => {
    setRows((prev) =>
      prev.map((row) =>
        row.applicationId !== applicationId
          ? row
          : kind === "legalCertificate"
            ? { ...row, pendingLegalCertificateFilename: filename }
            : { ...row, pendingRegistrationCertificateFilename: filename },
      ),
    );
  };

  const applyServerPreview = (data: LegalCertificateReceivedPreview) => {
    setPreview(data);
    setRows((prev) => mergeApplications(data.applications, prev));
  };

  const uploadFile = async (
    applicationId: string,
    kind: DocumentKind,
    file: File,
  ) => {
    if (!leadId) return;
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`${file.name} exceeds the 50MB limit`);
      return;
    }
    setPendingFilename(applicationId, kind, file.name);
    setUploading(applicationId, kind, true);
    try {
      const [presigned] = await generateUploadPresignedUrls(
        [{ filename: file.name, mimeType: file.type || "application/pdf" }],
        "private",
        `${UPLOAD_FOLDER_PREFIX}/${leadId}/${applicationId}`,
      );
      if (!presigned?.url || !presigned?.key) {
        throw new Error("Failed to generate upload URL");
      }

      const putRes = await fetch(presigned.url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/pdf" },
      });
      if (!putRes.ok) {
        throw new Error(`Failed to upload ${file.name}`);
      }

      const data = await saveLegalCertificateReceived(leadId, {
        applications: [
          kind === "legalCertificate"
            ? {
                applicationId,
                legalCertificatePdfFileKey: presigned.key,
                legalCertificatePdfFilename: file.name,
              }
            : {
                applicationId,
                registrationCertificatePdfFileKey: presigned.key,
                registrationCertificatePdfFilename: file.name,
              },
        ],
      });
      applyServerPreview(data);
      toast.success(`Uploaded ${file.name}`);
    } catch (err) {
      setPendingFilename(applicationId, kind, null);
      toast.error(err instanceof Error ? err.message : "Failed to upload file");
    } finally {
      setUploading(applicationId, kind, false);
    }
  };

  const removeUploadedPdf = async (
    applicationId: string,
    kind: DocumentKind,
  ) => {
    if (!leadId) return;
    try {
      const data = await saveLegalCertificateReceived(leadId, {
        applications: [
          kind === "legalCertificate"
            ? {
                applicationId,
                legalCertificatePdfFileKey: null,
                legalCertificatePdfFilename: null,
              }
            : {
                applicationId,
                registrationCertificatePdfFileKey: null,
                registrationCertificatePdfFilename: null,
              },
        ],
      });
      applyServerPreview(data);
      toast.success("File removed.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to remove file");
    }
  };

  const openPreview = (filename: string | null, url: string | null) => {
    if (!url || !filename) {
      toast.error("Preview is not available yet.");
      return;
    }
    setPreviewState({ url, name: filename, kind: detectPreviewKind(filename) });
  };

  const closePreview = () => setPreviewState(null);

  const handleDownload = async (
    filename: string | null,
    url: string | null,
  ) => {
    if (!url || !filename) {
      toast.error("No file available to download.");
      return;
    }
    try {
      const result = await downloadFileFromUrlWithFallback(url, filename);
      if (result === "local") {
        toast.success("File saved to your device.");
      } else {
        toast.info(
          "Opened the file in a new tab. Use Save or Save as to store it locally.",
        );
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to download file",
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    if (!preview) {
      toast.error("Status data not loaded yet. Please try again.");
      return;
    }
    if (rows.length === 0) {
      toast.error(
        "No applications found. Complete Application Details (Step 2) first.",
      );
      return;
    }
    const anyUploaded = rows.some((row) => row.isLegalCertificateUploaded);
    if (!anyUploaded) {
      toast.error(
        "Please upload the scanned Legal Certificate PDF for at least one application.",
      );
      return;
    }
    setSaving(true);
    try {
      const data = await saveLegalCertificateReceived(leadId, {
        applications: rows.map((row) => ({ applicationId: row.applicationId })),
      });
      applyServerPreview(data);
      toast.success("Status of Legal Certificate saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to
          access this form.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Loading Status of Legal Certificate data...
        </p>
      </div>
    );
  }

  if (!preview) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Application details not found. Please complete the previous steps
          first.
        </p>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          No applications found for this lead. Add applications in Application
          Details (Step 2) first.
        </p>
      </div>
    );
  }

  return (
    <form
      id={LEGAL_CERTIFICATE_STATUS_OF_LEGAL_CERTIFICATE_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 md:gap-5 w-full"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col gap-1.5">
        <p
          className="font-semibold text-[#101111]"
          style={{
            fontSize: "14px",
            lineHeight: "100%",
            letterSpacing: "0.01em",
          }}
        >
          Upload Documents
        </p>
        <p
          className="font-normal text-[#7D7D7D]"
          style={{
            fontSize: "12px",
            lineHeight: "20px",
            letterSpacing: "0.04em",
          }}
        >
          Choose a file and upload securely to proceed.
        </p>
      </div>

      {/* Desktop / tablet: table layout */}
      <div className="hidden md:block w-full overflow-x-auto">
        <table className="w-full border-separate border-spacing-y-2 min-w-[820px]">
          <thead>
            <tr>
              <th
                className="text-left text-xs font-normal text-[#222] px-1 pb-1 w-[220px]"
                style={LABEL_FEATURES}
              >
                TM Number
              </th>
              <th
                className="text-left text-xs font-normal text-[#222] px-1 pb-1"
                style={LABEL_FEATURES}
              >
                Legal Certificate PDF
              </th>
              <th
                className="text-left text-xs font-normal text-[#222] px-1 pb-1"
                style={LABEL_FEATURES}
              >
                Registration Certificate PDF
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const tmDisplay = row.tmNo || "—";
              return (
                <tr key={row.applicationId} className="align-top">
                  <td className="px-1">
                    <div
                      className="box-border flex flex-row items-center gap-2.5 px-[10px] py-2 h-9 w-full bg-[#F7F7F7] border border-[#EEEEEE] rounded-[6px] text-[12px] font-normal leading-5 text-[#222222] truncate"
                      style={LABEL_FEATURES}
                      title={row.mark ? `${tmDisplay} · ${row.mark}` : tmDisplay}
                    >
                      <span className="truncate">{tmDisplay}</span>
                    </div>
                  </td>

                  <td className="px-1">
                    <UploadCell
                      kind="legalCertificate"
                      inputIdSuffix={row.applicationId}
                      filename={
                        row.pendingLegalCertificateFilename ??
                        row.legalCertificatePdfFilename ??
                        null
                      }
                      uploadedFilename={row.legalCertificatePdfFilename}
                      downloadUrl={row.legalCertificateDownloadUrl}
                      uploading={isUploading(row.applicationId, "legalCertificate")}
                      dragging={
                        draggingKey === `${row.applicationId}-legalCertificate`
                      }
                      onDragEnter={() =>
                        setDraggingKey(`${row.applicationId}-legalCertificate`)
                      }
                      onDragLeave={() => setDraggingKey(null)}
                      onFileSelected={(file) =>
                        void uploadFile(row.applicationId, "legalCertificate", file)
                      }
                      onPreview={() =>
                        openPreview(
                          row.legalCertificatePdfFilename,
                          row.legalCertificateDownloadUrl,
                        )
                      }
                      onDownload={() =>
                        void handleDownload(
                          row.legalCertificatePdfFilename,
                          row.legalCertificateDownloadUrl,
                        )
                      }
                      onRemove={() =>
                        void removeUploadedPdf(row.applicationId, "legalCertificate")
                      }
                    />
                  </td>

                  <td className="px-1">
                    <UploadCell
                      kind="registrationCertificate"
                      inputIdSuffix={row.applicationId}
                      filename={
                        row.pendingRegistrationCertificateFilename ??
                        row.registrationCertificatePdfFilename ??
                        null
                      }
                      uploadedFilename={row.registrationCertificatePdfFilename}
                      downloadUrl={row.registrationCertificateDownloadUrl}
                      uploading={isUploading(
                        row.applicationId,
                        "registrationCertificate",
                      )}
                      dragging={
                        draggingKey ===
                        `${row.applicationId}-registrationCertificate`
                      }
                      onDragEnter={() =>
                        setDraggingKey(
                          `${row.applicationId}-registrationCertificate`,
                        )
                      }
                      onDragLeave={() => setDraggingKey(null)}
                      onFileSelected={(file) =>
                        void uploadFile(
                          row.applicationId,
                          "registrationCertificate",
                          file,
                        )
                      }
                      onPreview={() =>
                        openPreview(
                          row.registrationCertificatePdfFilename,
                          row.registrationCertificateDownloadUrl,
                        )
                      }
                      onDownload={() =>
                        void handleDownload(
                          row.registrationCertificatePdfFilename,
                          row.registrationCertificateDownloadUrl,
                        )
                      }
                      onRemove={() =>
                        void removeUploadedPdf(
                          row.applicationId,
                          "registrationCertificate",
                        )
                      }
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile: stacked cards */}
      <div className="md:hidden flex flex-col gap-4">
        {rows.map((row) => {
          const tmDisplay = row.tmNo || "—";
          return (
            <div
              key={row.applicationId}
              className="flex flex-col gap-3 p-4 rounded-[10px] border border-[#EEE] bg-white"
            >
              <div className="flex items-center gap-2 text-xs font-semibold text-[#222] flex-wrap">
                <span>TM No.</span>
                <span className="text-[#101828]">{tmDisplay}</span>
                {row.mark ? (
                  <span className="text-[#6D6D6D] font-normal">· {row.mark}</span>
                ) : null}
              </div>

              <div className="flex flex-col gap-1.5">
                <label
                  className="text-xs font-normal text-[#222]"
                  style={LABEL_FEATURES}
                >
                  Legal Certificate PDF
                </label>
                <UploadCell
                  mobile
                  kind="legalCertificate"
                  inputIdSuffix={`m-${row.applicationId}`}
                  filename={
                    row.pendingLegalCertificateFilename ??
                    row.legalCertificatePdfFilename ??
                    null
                  }
                  uploadedFilename={row.legalCertificatePdfFilename}
                  downloadUrl={row.legalCertificateDownloadUrl}
                  uploading={isUploading(row.applicationId, "legalCertificate")}
                  dragging={
                    draggingKey === `m-${row.applicationId}-legalCertificate`
                  }
                  onDragEnter={() =>
                    setDraggingKey(`m-${row.applicationId}-legalCertificate`)
                  }
                  onDragLeave={() => setDraggingKey(null)}
                  onFileSelected={(file) =>
                    void uploadFile(row.applicationId, "legalCertificate", file)
                  }
                  onPreview={() =>
                    openPreview(
                      row.legalCertificatePdfFilename,
                      row.legalCertificateDownloadUrl,
                    )
                  }
                  onDownload={() =>
                    void handleDownload(
                      row.legalCertificatePdfFilename,
                      row.legalCertificateDownloadUrl,
                    )
                  }
                  onRemove={() =>
                    void removeUploadedPdf(row.applicationId, "legalCertificate")
                  }
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label
                  className="text-xs font-normal text-[#222]"
                  style={LABEL_FEATURES}
                >
                  Registration Certificate PDF
                </label>
                <UploadCell
                  mobile
                  kind="registrationCertificate"
                  inputIdSuffix={`m-${row.applicationId}`}
                  filename={
                    row.pendingRegistrationCertificateFilename ??
                    row.registrationCertificatePdfFilename ??
                    null
                  }
                  uploadedFilename={row.registrationCertificatePdfFilename}
                  downloadUrl={row.registrationCertificateDownloadUrl}
                  uploading={isUploading(
                    row.applicationId,
                    "registrationCertificate",
                  )}
                  dragging={
                    draggingKey ===
                    `m-${row.applicationId}-registrationCertificate`
                  }
                  onDragEnter={() =>
                    setDraggingKey(
                      `m-${row.applicationId}-registrationCertificate`,
                    )
                  }
                  onDragLeave={() => setDraggingKey(null)}
                  onFileSelected={(file) =>
                    void uploadFile(
                      row.applicationId,
                      "registrationCertificate",
                      file,
                    )
                  }
                  onPreview={() =>
                    openPreview(
                      row.registrationCertificatePdfFilename,
                      row.registrationCertificateDownloadUrl,
                    )
                  }
                  onDownload={() =>
                    void handleDownload(
                      row.registrationCertificatePdfFilename,
                      row.registrationCertificateDownloadUrl,
                    )
                  }
                  onRemove={() =>
                    void removeUploadedPdf(
                      row.applicationId,
                      "registrationCertificate",
                    )
                  }
                />
              </div>
            </div>
          );
        })}
      </div>

      <FilePreviewModal
        open={!!previewState}
        onClose={closePreview}
        name={previewState?.name ?? ""}
        url={previewState?.url}
        kind={previewState?.kind}
      />
    </form>
  );
}

type UploadCellProps = {
  kind: DocumentKind;
  inputIdSuffix: string;
  filename: string | null;
  uploadedFilename: string | null;
  downloadUrl: string | null;
  uploading: boolean;
  dragging: boolean;
  mobile?: boolean;
  onDragEnter: () => void;
  onDragLeave: () => void;
  onFileSelected: (file: File) => void;
  onPreview: () => void;
  onDownload: () => void;
  onRemove: () => void;
};

function UploadCell({
  kind,
  inputIdSuffix,
  filename,
  uploadedFilename,
  downloadUrl,
  uploading,
  dragging,
  mobile,
  onDragEnter,
  onDragLeave,
  onFileSelected,
  onPreview,
  onDownload,
  onRemove,
}: UploadCellProps) {
  const inputId = `lc-status-input-${kind}-${inputIdSuffix}`;
  const hasFile = !!filename;

  return (
    <div className="flex flex-col gap-2">
      <div
        className={`flex flex-row justify-between items-center gap-3 ${
          mobile ? "px-3 py-3 rounded-[10px]" : "px-3 py-2 rounded-[8px]"
        } border border-dashed bg-white transition-colors cursor-pointer ${
          dragging
            ? "border-[#222] bg-[#F5F5F5]"
            : hasFile
              ? "border-[#22C55E] bg-[#F0FDF4]"
              : "border-[#D8DAEB]"
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          onDragEnter();
        }}
        onDragLeave={onDragLeave}
        onDrop={(e) => {
          e.preventDefault();
          onDragLeave();
          if (uploading) return;
          const file = e.dataTransfer.files?.[0];
          if (file) onFileSelected(file);
        }}
        onClick={() => {
          if (uploading) return;
          document.getElementById(inputId)?.click();
        }}
      >
        <div className="flex flex-row items-center gap-3 min-w-0">
          <img
            src={uploadDropzoneGraphic}
            alt=""
            width={mobile ? 40 : 32}
            height={mobile ? 40 : 32}
            className="flex-shrink-0"
          />
          <div className="flex flex-col gap-0.5 min-w-0">
            {hasFile ? (
              <>
                <p
                  className={`font-medium text-[#16A34A] truncate ${
                    mobile ? "text-[13px] leading-5" : "text-[12px] leading-4"
                  }`}
                  title={filename ?? ""}
                >
                  {filename}
                </p>
                <p
                  className={`text-[#7D7D7D] ${
                    mobile ? "text-[11px]" : "text-[10px] leading-3"
                  }`}
                >
                  {uploading ? "Uploading…" : ACCEPTED_HELP}
                </p>
              </>
            ) : (
              <>
                <p
                  className={`font-medium text-[#101111] ${
                    mobile ? "text-[13px] leading-5" : "text-[12px] leading-4"
                  }`}
                >
                  Drag and drop your files
                </p>
                <p
                  className={`text-[#7D7D7D] ${
                    mobile ? "text-[11px]" : "text-[10px] leading-3"
                  }`}
                >
                  {uploading ? "Uploading…" : ACCEPTED_HELP}
                </p>
              </>
            )}
          </div>
        </div>
        <button
          type="button"
          disabled={uploading}
          onClick={(e) => {
            e.stopPropagation();
            document.getElementById(inputId)?.click();
          }}
          className={`flex-shrink-0 flex items-center justify-center ${
            mobile ? "h-9" : "h-8"
          } px-3 rounded-[8px] bg-white border border-[#D5D3E0] text-[#2E3037] hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed text-[12px] font-medium`}
          style={
            mobile
              ? undefined
              : { boxShadow: "0px 5px 20px rgba(0, 0, 0, 0.02)" }
          }
        >
          {hasFile ? "Replace" : "Select File"}
        </button>
      </div>

      <input
        id={inputId}
        type="file"
        accept={ACCEPTED_TYPES}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFileSelected(file);
          e.currentTarget.value = "";
        }}
      />

      {uploadedFilename && downloadUrl && (
        <div className="flex items-center justify-end gap-2 flex-wrap">
          <button
            type="button"
            onClick={onPreview}
            className={`${
              mobile ? "px-3 h-8 text-[12px]" : "px-2.5 h-7 text-[11px]"
            } rounded-[6px] border border-[#D5D3E0] bg-white font-medium text-[#2E3037] hover:bg-gray-50`}
          >
            Preview
          </button>
          <button
            type="button"
            onClick={onDownload}
            className={`${
              mobile ? "px-3 h-8 text-[12px]" : "px-2.5 h-7 text-[11px]"
            } rounded-[6px] border border-[#D5D3E0] bg-white font-medium text-[#2E3037] hover:bg-gray-50`}
          >
            Download
          </button>
          <button
            type="button"
            aria-label={`Remove ${uploadedFilename}`}
            onClick={onRemove}
            className="text-[#6D6D6D] hover:text-[#222] p-1"
            title="Remove"
          >
            <HiOutlineX
              className={mobile ? "w-4 h-4" : "w-3.5 h-3.5"}
              strokeWidth={2}
            />
          </button>
        </div>
      )}
    </div>
  );
}
